import { useEffect, useRef, useState } from "react";
import { useLiveFeed } from "@/stores/liveFeed";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { scaleApparent, scalePower } from "@/lib/units";
import { PqEventBadge } from "@/components/PqEventBadge";
import { usePqTuningStore } from "@/stores/pqTuningStore";
import type { Service } from "@/lib/types";
import { meterModelLabel } from "@/lib/meter";
import SignalBars from "@/components/SignalBars";
import { useModemSignals } from "@/lib/modemSignals";

// Clickable derived-field label. The whole label + tiny mark is the click
// target; clicking toggles a popover explaining what the value is derived
// from. Used to distinguish kinds of "derived" values on a tile: single
// * = computed from base measurements (e.g. VA/PF from P+Q); double
// ** = rolling-average derived (e.g. Demand from 15-min average of active
// power).
//
// Positioning: uses position:fixed with manual coordinate clamping so the
// popover stays inside the viewport regardless of which tile/column the
// trigger lives in. Tiles in the left column of the grid would have
// right-anchored popovers clip off the left edge of the screen; the
// clamp via getBoundingClientRect avoids that.
function DerivedFieldLabel({
  label,
  unit,
  mark,
  body,
}: {
  label: string;             // e.g. "Demand", "VA", "PF"
  unit?: string;             // e.g. "kW", "kVA" — shown small after label
  mark: string;              // "*" or "**"
  body: React.ReactNode;     // explanation (JSX allowed)
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  useEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    function place() {
      const t = triggerRef.current?.getBoundingClientRect();
      if (!t) return;
      const padding = 8;
      const popWidth = 256; // w-64
      // Anchor to trigger's left edge; clamp right side to viewport.
      let left = t.left;
      if (left + popWidth > window.innerWidth - padding) {
        left = window.innerWidth - popWidth - padding;
      }
      if (left < padding) left = padding;
      const top = t.bottom + 4;
      setPos({ left, top });
    }
    place();
    const onResize = () => place();
    const onScroll = () => place();
    // Outside-click dismissal handled by the invisible backdrop element
    // rendered below; document-level mousedown listener would conflict
    // with the tile-onClick propagation control we want.
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, true);
    document.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();  // don't trigger tile onClick
          setOpen((v) => !v);
        }}
        // Whole "Label**" is the click target. Inherits the surrounding
        // text-[10px] uppercase tracking-wide treatment from the parent so
        // it looks indistinguishable from a non-clickable label except
        // for the dotted underline (signaling click affordance) and the
        // tiny amber mark suffix.
        className="inline-flex items-baseline gap-0.5 underline decoration-dotted decoration-amber-400/60 underline-offset-2 hover:decoration-amber-300 hover:text-foreground"
        aria-label={`${label} — calculation explanation`}
      >
        <span>{label}</span>
        {unit && (
          <span className="text-[8px] normal-case text-muted-foreground/80 font-normal">{unit}</span>
        )}
        <span className="text-[7px] text-amber-400 align-baseline leading-none translate-y-[-1px]">{mark}</span>
      </button>
      {open && pos && (
        <>
          {/* Invisible full-viewport backdrop catches any click outside the
              popover and the trigger, closes the popover, and crucially
              stops the click from propagating to the tile underneath (which
              would otherwise navigate to system details). */}
          <div
            className="fixed inset-0 z-40"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
            }}
          />
          <div
            ref={popRef}
            role="tooltip"
            // bg-zinc-900 is opaque against any tile sig-tint (emerald/amber/
            // muted/40 alpha). Theme doesn't define --popover so we use a
            // concrete value. position:fixed + manual coords keep it inside
            // the viewport regardless of tile placement.
            style={{ left: pos.left, top: pos.top }}
            className="fixed z-50 w-64 rounded-md border border-zinc-700 bg-zinc-900 px-3 py-2 text-[11px] leading-snug text-zinc-50 shadow-xl not-italic font-normal normal-case whitespace-normal"
            onClick={(e) => e.stopPropagation()}
          >
            {body}
          </div>
        </>
      )}
    </>
  );
}

const VA_EXPLAIN = (
  <>
    <div className="font-semibold mb-1">Apparent power (VA)</div>
    <div>
      Calculated as <code className="text-amber-300">√(P² + Q²)</code> from
      active power (P) and reactive power (Q). This meter doesn't report VA
      directly.
    </div>
  </>
);

const PF_EXPLAIN = (
  <>
    <div className="font-semibold mb-1">Power factor (PF)</div>
    <div>
      Calculated as <code className="text-amber-300">P / √(P² + Q²)</code> at
      the system total, signed by the direction of P (negative = exporting).
      This meter doesn't report PF directly.
    </div>
    <div className="mt-1.5 text-zinc-400">
      Other industry methods exist (true PF including harmonics, displacement
      PF at fundamental, per-phase aggregation rules) — this one matches
      what's stored in the metric.
    </div>
  </>
);

const DEMAND_EXPLAIN = (
  <>
    <div className="font-semibold mb-1">Demand (15-min rolling average)</div>
    <div>
      Calculated as the rolling 15-minute average of active power, sampled
      every 10 seconds and averaged in a sliding window. This meter doesn't
      expose a built-in demand register.
    </div>
  </>
);

// Per-service tile. Subscribes to the live-feed store via primitive-returning
// selectors so unchanged ticks don't re-render the whole grid (research §B
// "per-field primitive selectors in the hot path" — at 5 Hz this is the
// difference between a smooth grid and a re-render storm).

function fmt(v: number | undefined | null, digits = 1): string {
  if (v == null) return "—";
  return v.toFixed(digits);
}

// scalePower / scaleApparent moved to @/lib/units so non-tile consumers
// (TotalLoadWidget) can reuse them without importing the chart stack.

const SIG_STYLES: Record<string, string> = {
  ok: "bg-emerald-500/20 border-emerald-500/40",
  stale: "bg-muted border-border opacity-60",
};

// NEC 210.19(A)(1) / 210.20(A) — continuous-load conductor + OCPD sizing at
// 125% of load, which is the algebraic inverse of an 80% usable ceiling on
// standard (non-100%-rated) breakers. 100% = trip ceiling. These thresholds
// are prescriptive, not stylistic.
const PCT_AMBER = 80;
const PCT_RED = 100;

function pctTextClass(pct: number | null): string {
  if (pct == null) return "";
  if (pct >= PCT_RED) return "text-red-500";
  if (pct >= PCT_AMBER) return "text-amber-400";
  return "text-emerald-400";
}

// Deep-link to a node's ACUVIM 3-phase Grafana dashboard with the same
// defaults the dashboard ships with — 3 h window, edge-vm datasource,
// 30 s refresh. var-system_id stays at $__all so clicking node-N takes
// the operator to the whole node view; per-system drill-down is the
// tile body's own click into ServiceDetail. The slug `acuvim-e28094-…`
// is what Grafana generates from the title's em-dash; the /d/<uid>
// portion alone is enough for Grafana to resolve, but the full slug
// keeps the URL stable against any future title tweaks.
function nodeGrafanaHref(nodeId: string): string {
  return (
    `https://${nodeId}-grafana.overdrive.rocks` +
    `/d/acuvim-3phase/acuvim-e28094-3-phase-currents` +
    `?orgId=1&from=now-3h&to=now&timezone=browser` +
    `&var-ds=edge-vm&var-node=${nodeId}` +
    `&var-system_id=$__all&refresh=30s`
  );
}

// US 3-phase conductor color code (NEC 120/208V & 277/480V both run
// black / red / blue for the first three legs, white for neutral). The
// bar carries phase identity; the amps value to its right keeps the
// green/amber/red NEC-% treatment so headroom still reads. The track
// stays a subtle dark so the empty bars don't read as heavy; the black L1
// fill is defined by a hairline ring rather than by track contrast. The
// neutral bar uses an off-white that stays legible against the muted
// track without competing visually with the three phase bars.
const PHASE_BAR_CLASS = ["bg-black", "bg-red-500", "bg-blue-500"];
const PHASE_BAR_RING = ["ring-1 ring-inset ring-zinc-400/60", "", ""];
const NEUTRAL_BAR_CLASS = "bg-zinc-200";
const NEUTRAL_BAR_RING = "ring-1 ring-inset ring-zinc-400/60";

// Per-phase instantaneous current bars (L1/L2/L3, plus N when the meter
// reports it). Each bar fills toward the service's rated amps and is colored
// by phase identity (black/red/blue/white) so imbalance reads at a glance;
// the per-leg amps value carries the NEC %. Neutral skips the NEC color —
// it's not breaker-rated the same way; the bar height itself is the imbalance
// signal. When capacity is unknown the bars scale to the hottest leg.
//
// This replaces the old three-line overlapping sparkline: three near-identical
// phase traces collapsed into one visual line on a balanced load, so a
// 3-line chart couldn't actually convey per-phase info at tile size. Three
// instantaneous per-phase bars at full tile width carry the load picture on
// their own.
function PhaseBars({
  ia,
  ib,
  ic,
  iN,
  capacityAmps,
}: {
  ia: number | undefined;
  ib: number | undefined;
  ic: number | undefined;
  iN: number | undefined;
  capacityAmps: number | null;
}) {
  const rated = capacityAmps && capacityAmps > 0 ? capacityAmps : null;
  const peak = Math.max(0, ia ?? 0, ib ?? 0, ic ?? 0, iN ?? 0);
  // Bar scale: rated amps when known, else the hottest leg + 15% headroom so
  // the top bar isn't pinned full. `|| 1` guards a degenerate all-zero scale.
  const scaleMax = rated ?? (peak * 1.15 || 1);
  const phases: Array<[string, number | undefined]> = [
    ["L1", ia],
    ["L2", ib],
    ["L3", ic],
  ];
  return (
    <div className="flex h-full flex-col justify-center gap-1">
      {phases.map(([label, v], idx) => {
        // pct (vs rated) drives the NEC color on the amps value; width is
        // scaled separately so bars stay visible even on a service running
        // well below its breaker. The bar fill color is phase identity.
        const pct = rated != null && v != null ? (v / rated) * 100 : null;
        const width = v != null ? Math.min(100, (v / scaleMax) * 100) : 0;
        return (
          <div
            key={label}
            className="flex items-center gap-1.5 text-[10px] tabular-nums"
          >
            <span className="w-4 text-muted-foreground">{label}</span>
            <div className="h-2 flex-1 overflow-hidden rounded bg-muted">
              <div
                className={cn(
                  "h-full transition-all",
                  PHASE_BAR_CLASS[idx],
                  PHASE_BAR_RING[idx]
                )}
                style={{ width: `${width}%` }}
              />
            </div>
            <span className={cn("w-9 text-right font-medium", pctTextClass(pct))}>
              {v != null ? `${v.toFixed(0)}A` : "—"}
            </span>
          </div>
        );
      })}
      {iN != null && (
        <div className="flex items-center gap-1.5 text-[10px] tabular-nums">
          <span className="w-4 text-muted-foreground">N</span>
          <div className="h-2 flex-1 overflow-hidden rounded bg-muted">
            <div
              className={cn("h-full transition-all", NEUTRAL_BAR_CLASS, NEUTRAL_BAR_RING)}
              style={{ width: `${Math.min(100, (iN / scaleMax) * 100)}%` }}
            />
          </div>
          <span className="w-9 text-right font-medium text-muted-foreground">
            {`${iN.toFixed(0)}A`}
          </span>
        </div>
      )}
    </div>
  );
}

export function ServiceTile({
  staticService,
  onClick,
}: {
  staticService: Service;
  onClick?: () => void;
}) {
  const systemId = staticService.system_id;
  const p = useLiveFeed((s) => s.services[systemId]?.p);
  const sTotal = useLiveFeed((s) => s.services[systemId]?.s);
  const pf = useLiveFeed((s) => s.services[systemId]?.pf);
  const f = useLiveFeed((s) => s.services[systemId]?.f);
  const demandW = useLiveFeed((s) => s.services[systemId]?.demand_w ?? null);
  const demandSource = useLiveFeed((s) => s.services[systemId]?.demand_source ?? null);
  const meterModel = useLiveFeed((s) => s.services[systemId]?.meter_model ?? null);
  const meterLabel = meterModelLabel(meterModel);
  // Live node_id — systems float between nodes during an event. Fall back to
  // the static value so an empty live feed (pre-first-snapshot) still shows
  // the last-known node from /api/services.
  const liveNodeId = useLiveFeed((s) => s.services[systemId]?.node_id ?? null);
  const nodeId = liveNodeId ?? staticService.node_id ?? null;
  // LTE signal of the node this service is currently on (modems float with the
  // node). Polled from /api/modems by the shared store; null for wired nodes.
  useEffect(() => {
    useModemSignals.getState().start();
  }, []);
  const modem = useModemSignals((s) => (nodeId ? s.byNode[nodeId] ?? null : null));
  // "acuvim-939e" alongside a model label of "Acuvim 3" is just redundant —
  // strip the prefix when the model is known so the tile reads as
  // "Acuvim 3 / node-7 / 939e" instead of "Acuvim 3 / node-7 / acuvim-939e".
  // Fall back to the full id when no model is known yet.
  const shortId = meterLabel && systemId.startsWith("acuvim-")
    ? systemId.slice("acuvim-".length)
    : systemId;
  const sig = useLiveFeed((s) => s.services[systemId]?.sig ?? "ok");
  // U6 — data-presence detector state. 'silent' / 'vm_broken' OVERRIDE the
  // sig stale dimming: a service whose cloud-side detector classifies as
  // silent is a more urgent signal than the source MQTT being stale, and
  // the tile must render at full opacity with a prominent red border so
  // Sean spots it without scrolling. See plan §"Visual treatment".
  const presence = useLiveFeed(
    (s) => s.services[systemId]?.data_presence_state ?? null,
  );
  const lastSampleAt = useLiveFeed(
    (s) => s.services[systemId]?.ts ?? null,
  );
  const computedFields = useLiveFeed((s) =>
    (s.services[systemId]?.computed_fields ?? []).join(",")
  );
  const computed = new Set(computedFields ? computedFields.split(",") : []);
  const pfComputed = computed.has("pf");
  const sComputed = computed.has("s_total");
  // Derived demand also gets the asterisk treatment so ops can distinguish
  // meter-grade readings from cloud-computed averages at a glance.
  // *  (single) = computed from base measurements (P+Q → VA/PF)
  // ** (double) = rolling-average derived (15-min avg → Demand)
  const demandDerived = demandSource === "derived";
  // Per-phase amps drive the "% capacity" gauge (peak-leg ÷ rated). The
  // sparkline subscribes to the same fields itself on its own 4 Hz timer.
  const ia = useLiveFeed((s) => s.services[systemId]?.i?.[0]);
  const ib = useLiveFeed((s) => s.services[systemId]?.i?.[1]);
  const ic = useLiveFeed((s) => s.services[systemId]?.i?.[2]);
  const iN = useLiveFeed((s) => s.services[systemId]?.i_n);

  // PQ-tuning badge fields are live (dyno_snapshot WS) — they are NOT in the
  // static /api/services fetch, so they must be read from liveFeed, not from
  // staticService, or the badge never appears.
  const inominalStatus = useLiveFeed((s) => s.services[systemId]?.inominal_status ?? null);
  const flagged = useLiveFeed((s) => s.services[systemId]?.flagged ?? null);
  const flagReason = useLiveFeed((s) => s.services[systemId]?.reason ?? null);
  const dominantSeverity = useLiveFeed((s) => s.services[systemId]?.dominant_severity ?? null);
  const eventsLastHour = useLiveFeed((s) => s.services[systemId]?.events_last_hour ?? null);

  const capacityAmps = staticService.service_capacity_amps ?? null;
  const peakLeg = Math.max(ia ?? 0, ib ?? 0, ic ?? 0);
  // Peak-leg ÷ rated: it's the leg that trips the breaker, not the average.
  const capacityPct =
    capacityAmps && capacityAmps > 0 && (ia != null || ib != null || ic != null)
      ? (peakLeg / capacityAmps) * 100
      : null;

  const hasLive = p != null || ia != null;

  // Acuvim systems get a PQ-tuning badge in the top-right corner. Click
  // opens the tuning dialog rendered at the Dashboard root.
  const isAcuvim = (staticService.system_type ?? "").toLowerCase() === "acuvim";
  const openPqDialog = usePqTuningStore((s) => s.open);

  // Silent / vm_broken state overrides the sig stale dimming so the urgent
  // signal isn't visually de-emphasized by the soft "stale" treatment.
  const isSilent = presence === "silent";
  const isVmBroken = presence === "vm_broken";
  const presenceClass = isSilent || isVmBroken
    ? "border-red-500/80 ring-1 ring-red-500/40 opacity-100"
    : "";

  return (
    <Card
      onClick={onClick}
      className={cn(
        "relative cursor-pointer transition-colors border-2",
        hasLive ? SIG_STYLES[sig] ?? "" : "hover:border-primary/40",
        presenceClass,
      )}
    >
      <CardHeader className="pb-2">
        {/* Left: service name (+ PQ badge inline) + capacity spec. Right:
            system_id + meter model stacked in the corner. Identity (right)
            is visually subordinate to the operator-facing name + rating. */}
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            {/* Badge sits inline next to the name — an actionable status
                marker on the most prominent element, not floating over the
                system_id text in the corner. */}
            <div className="flex items-center gap-1.5">
              <CardTitle className="min-w-0 truncate text-base">
                {staticService.service_name ?? (
                  <span className="text-muted-foreground">(unnamed)</span>
                )}
              </CardTitle>
              {isAcuvim && (
                <PqEventBadge
                  service={{
                    system_id: systemId,
                    inominal_status: inominalStatus,
                    flagged,
                    reason: flagReason,
                    dominant_severity: dominantSeverity,
                    events_last_hour: eventsLastHour,
                  }}
                  onClick={() => openPqDialog(systemId)}
                />
              )}
              {(isSilent || isVmBroken) && (
                <span
                  className={cn(
                    "inline-flex items-center gap-1 rounded-full px-2 py-0.5",
                    "text-[10px] font-semibold uppercase tracking-wide",
                    "bg-red-500/15 text-red-400 border border-red-500/40",
                  )}
                  title={
                    isVmBroken
                      ? "Edge VM unreachable; node-side debug"
                      : lastSampleAt
                        ? `Silent — last sample ${Math.floor((Date.now() - lastSampleAt) / 60000)}m ago`
                        : "Silent — no recent samples"
                  }
                >
                  {isVmBroken ? "VM broken" : "Silent"}
                </span>
              )}
            </div>
            {capacityAmps != null && (
              <div className="text-xs text-muted-foreground">
                {capacityAmps}A spec
              </div>
            )}
          </div>
          {/* Two-line identity stack: model on top, node-# / short-id below.
              The "acuvim-" prefix is dropped from the system id when a model
              label is present to avoid restating it. */}
          <div className="shrink-0 max-w-[48%] text-right text-[10px] leading-tight text-muted-foreground">
            {meterLabel && <div className="truncate">{meterLabel}</div>}
            <div className="truncate font-mono">
              {nodeId ? (
                <>
                  {/* node-N → that node's ACUVIM 3-Phase Currents Grafana
                      dashboard (see nodeGrafanaHref above). stopPropagation
                      keeps the click from also firing the Card's
                      tile-into-detail navigation; target=_blank opens
                      Grafana in a new tab. */}
                  <a
                    href={nodeGrafanaHref(nodeId)}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="hover:underline hover:text-foreground"
                    title={`Open ${nodeId} Grafana — ACUVIM 3-Phase Currents`}
                  >
                    {nodeId}
                  </a>
                  {modem ? <>{" "}<SignalBars modem={modem} /></> : null}
                  {" / "}{shortId}
                </>
              ) : (
                shortId
              )}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0 space-y-2">
        {hasLive ? (
          <>
            {capacityAmps != null && (
              <div
                title={`Peak-leg ${peakLeg.toFixed(0)}A of ${capacityAmps}A rated. Amber at NEC continuous (80%); red at trip ceiling (100%).`}
              >
                <div className="flex items-baseline gap-1.5 tabular-nums">
                  <span className={cn("text-3xl font-semibold leading-none", pctTextClass(capacityPct))}>
                    {capacityPct != null ? peakLeg.toFixed(0) : "—"}
                  </span>
                  <span className="text-xs text-muted-foreground">A</span>
                  <span className="ml-auto text-[10px] text-muted-foreground">
                    {capacityPct != null
                      ? `${capacityPct.toFixed(1)}% of ${capacityAmps} A`
                      : `${capacityAmps} A`}
                  </span>
                </div>
              </div>
            )}
            {/* Per-phase load: instantaneous L1/L2/L3 current bars, full tile
                width. The headline above is the peak leg of these three in
                amps; a separate progress bar would just restate the longest
                bar. */}
            <PhaseBars ia={ia} ib={ib} ic={ic} iN={iN} capacityAmps={capacityAmps} />
            {/* Bottom strip: Demand kW / Instant kW / VA kVA / PF / Hz
                — flex (not grid) so PF + Hz cells shrink to content, leaving
                more room for the wider Demand/Instant/VA columns. Units live
                inside the metric name as a small suffix; the value column
                shows only the number. */}
            <div className="flex gap-3 text-xs tabular-nums border-t border-border pt-1.5">
              {demandW != null && (() => {
                const sc = scalePower(demandW);
                return (
                  <div className="flex-1 min-w-0">
                    <div className="text-muted-foreground text-[10px] uppercase tracking-wide whitespace-nowrap">
                      {demandDerived ? (
                        <DerivedFieldLabel label="Demand" unit={sc.unit} mark="**" body={DEMAND_EXPLAIN} />
                      ) : (
                        <>Demand <span className="text-[8px] normal-case text-muted-foreground/80">{sc.unit}</span></>
                      )}
                    </div>
                    <div className={cn("font-medium", demandDerived && "text-amber-300/90")}>
                      {sc.value.toFixed(sc.digits)}
                    </div>
                  </div>
                );
              })()}
              {(() => {
                const sc = p != null ? scalePower(p) : null;
                return (
                  <div className="flex-1 min-w-0">
                    <div className="text-muted-foreground text-[10px] uppercase tracking-wide whitespace-nowrap">
                      Instant <span className="text-[8px] normal-case text-muted-foreground/80">{sc?.unit ?? "W"}</span>
                    </div>
                    <div className="font-medium">
                      {sc ? sc.value.toFixed(sc.digits) : "—"}
                    </div>
                  </div>
                );
              })()}
              {sTotal != null && (() => {
                const sc = scaleApparent(sTotal);
                return (
                  <div className="flex-1 min-w-0">
                    <div className="text-muted-foreground text-[10px] uppercase tracking-wide whitespace-nowrap">
                      {sComputed ? (
                        // For VA the metric name IS the unit, so the unit
                        // string carries both jobs. Pass it as the label and
                        // omit the secondary unit suffix.
                        <DerivedFieldLabel label={sc.unit} mark="*" body={VA_EXPLAIN} />
                      ) : (
                        sc.unit
                      )}
                    </div>
                    <div className={cn("font-medium", sComputed && "text-amber-300/90")}>
                      {sc.value.toFixed(sc.digits)}
                    </div>
                  </div>
                );
              })()}
              <div className="shrink-0">
                <div className="text-muted-foreground text-[10px] uppercase tracking-wide">
                  {pfComputed ? (
                    <DerivedFieldLabel label="PF" mark="*" body={PF_EXPLAIN} />
                  ) : (
                    "PF"
                  )}
                </div>
                <div className={cn("font-medium", pfComputed && "text-amber-300/90")}>
                  {fmt(pf, 2)}
                </div>
              </div>
              <div className="shrink-0">
                {/* "Hz" — proper SI casing (capital H, lowercase z); the
                    uppercase utility would render as "HZ". */}
                <div className="text-muted-foreground text-[10px] tracking-wide">Hz</div>
                <div className="font-medium">{fmt(f, 1)}</div>
              </div>
            </div>
          </>
        ) : (
          <div className="text-xs text-muted-foreground py-2">
            — waiting for data —
          </div>
        )}
      </CardContent>
    </Card>
  );
}
